import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getStudentFee, updateFeeStatus } from '../../../redux/feeRelated/feeHandle';
import { getUserDetails } from '../../../redux/userRelated/userHandle';
import { underControl } from '../../../redux/feeRelated/feeSlice';
import {
    Box, Button, Container, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, TextField,
    MenuItem, Select, FormControl, InputLabel, Dialog, DialogTitle,
    DialogContent, DialogActions, Grid, Avatar, Stack, Divider,
    Tooltip, Card, useTheme, useMediaQuery, CircularProgress
} from '@mui/material';
import {
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
    Payment as PaymentIcon,
    AccountBalanceWallet as WalletIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
    ReceiptLong as ReceiptLongIcon,
    CalendarToday as CalendarTodayIcon,
    Info as InfoIcon,
    School as SchoolIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../../context/ToastContext';
import { CustomNepaliDatePicker, getMonthNameFromDate, formatDatePart, getCurrentBSDate } from '../../../utils/customDate';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const getFeeFinancials = (fee = {}) => {
    const cashPaid = Number(fee.paidAmount) || 0;
    const advancePaid = Number(fee.advanceUsed) || 0;
    const payableAmount = Number(fee.amount) || 0;
    const grossAmount = Number(fee.grossAmount) || Math.max(0, payableAmount + advancePaid);
    const collected = cashPaid + advancePaid;
    const due = Math.max(0, payableAmount - cashPaid);

    return {
        cashPaid,
        advancePaid,
        payableAmount,
        grossAmount,
        collected,
        due
    };
};

const ViewStudentFee = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const highlightMonth = location.state?.highlightMonth;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const studentID = params.id;
    const { userDetails, loading: studentLoading } = useSelector((state) => state.user);
    const { feesList, loading: feeLoading, status } = useSelector((state) => state.fee);

    const [feeData, setFeeData] = useState(null);
    const { showToast } = useToast();

    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingFee, setEditingFee] = useState(null);
    const [editStatus, setEditStatus] = useState('');
    const [editMonthlyAmount, setEditMonthlyAmount] = useState('');
    const [editDuesAmount, setEditDuesAmount] = useState('');
    const [editDiscountAmount, setEditDiscountAmount] = useState('');
    const [editPaidAmount, setEditPaidAmount] = useState('');
    const [editPaymentDate, setEditPaymentDate] = useState('');

    useEffect(() => {
        if (studentID) {
            dispatch(getUserDetails(studentID, "Student"));
            dispatch(getStudentFee(studentID));
        }
    }, [dispatch, studentID]);

    useEffect(() => {
        if (feesList && feesList.length > 0) {
            setFeeData(feesList[0]);
        }
    }, [feesList]);

    useEffect(() => {
        if (status === "added") {
            dispatch(underControl());
            dispatch(getStudentFee(studentID));
        }
    }, [status, dispatch, studentID]);

    const handleEditClick = (feeDetail) => {
        setEditingFee(feeDetail);
        setEditStatus(feeDetail.status);
        setEditMonthlyAmount(feeDetail.monthlyAmount || '');
        setEditDuesAmount(feeDetail.duesAmount || '');
        setEditDiscountAmount(feeDetail.discount || '');
        setEditPaidAmount(feeDetail.paidAmount || '');
        setEditPaymentDate(feeDetail.paymentDate ? formatDatePart(feeDetail.paymentDate) : '');
        setEditDialogOpen(true);
    };

    const handleSaveEdit = () => {
        if (editingFee) {
            const monthly = parseFloat(editMonthlyAmount) || 0;
            const dues = parseFloat(editDuesAmount) || 0;
            const discount = parseFloat(editDiscountAmount) || 0;
            const paid = parseFloat(editPaidAmount) || 0;
            const advanceUsed = parseFloat(editingFee.advanceUsed) || 0;
            const grossAmount = Math.max(0, monthly + dues - discount);
            const payableAmount = Math.max(0, grossAmount - advanceUsed);
            
            let finalPaid = paid;
            if (editStatus === 'Unpaid') finalPaid = 0;
            else if (editStatus === 'Paid' && paid === 0) finalPaid = payableAmount;

            const data = {
                month: editingFee.month,
                status: editStatus,
                monthlyAmount: monthly,
                duesAmount: dues,
                discount: discount,
                grossAmount,
                advanceUsed,
                amount: payableAmount,
                paidAmount: finalPaid,
                paymentDate: editPaymentDate || null
            };
            dispatch(updateFeeStatus(editingFee._id, data)).then(() => {
                showToast("Fee updated successfully!", "success");
            });
            setEditDialogOpen(false);
        }
    };

    const handleToggleStatus = (fee) => {
        const newStatus = fee.status === 'Paid' ? 'Unpaid' : 'Paid';
        const data = {
            ...fee,
            status: newStatus,
            paidAmount: newStatus === 'Paid' ? fee.amount : 0,
            paymentDate: newStatus === 'Paid' ? getCurrentBSDate() : null
        };
        dispatch(updateFeeStatus(fee._id, data)).then(() => {
            showToast(`Status changed to ${newStatus}`, "success");
        });
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'Paid': return { bg: '#e8f5e9', color: '#2e7d32', icon: <CheckCircleIcon fontSize="small" /> };
            case 'Partial': return { bg: '#fff3e0', color: '#ed6c02', icon: <PaymentIcon fontSize="small" /> };
            case 'Unpaid': return { bg: '#ffebee', color: '#d32f2f', icon: <WarningIcon fontSize="small" /> };
            default: return { bg: '#f5f5f5', color: '#757575', icon: <InfoIcon fontSize="small" /> };
        }
    };

    const calculateSummary = () => {
        const details = feeData?.feeDetails || [];
        const result = details.reduce((acc, f) => {
            const financials = getFeeFinancials(f);
            acc.expected += financials.grossAmount;
            acc.collected += financials.collected;
            acc.advanceUsed += financials.advancePaid;
            acc.outstanding += financials.due;
            return acc;
        }, { expected: 0, collected: 0, advanceUsed: 0, outstanding: 0 });

        return result;
    };

    const summary = calculateSummary();
    const collectionRate = summary.expected > 0 ? (summary.collected / summary.expected * 100).toFixed(1) : 0;

    if (studentLoading || feeLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
                <CircularProgress size={40} sx={{ color: '#667eea', mb: 2 }} />
                <Typography color="textSecondary">Loading student financial profile...</Typography>
            </Box>
        );
    }

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 4 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>Student Fee Profile</Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>Detailed financial history for {userDetails?.name}</Typography>
                        </Box>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: -4, pb: 10 }}>
                <Grid container spacing={3}>
                    {/* Student Info Card */}
                    <Grid item xs={12} md={4}>
                        <InfoCard elevation={3}>
                            <Box sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
                                <Avatar
                                    src={userDetails?.photo ? `${process.env.REACT_APP_BASE_URL}/${userDetails.photo}` : ''}
                                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2, border: '4px solid #f8fafc', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                                >
                                    {userDetails?.name?.[0]}
                                </Avatar>
                                <Typography variant="h5" fontWeight="800">{userDetails?.name}</Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>Student ID: {userDetails?._id?.slice(-8).toUpperCase()}</Typography>

                                <Divider sx={{ my: 2 }} />

                                <Stack spacing={2} sx={{ textAlign: 'left' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <IconBox color="#667eea"><SchoolIcon size="small" /></IconBox>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">Class & Roll</Typography>
                                            <Typography variant="body2" fontWeight="700">{userDetails?.sclassName?.sclassName} • Roll: {userDetails?.rollNum}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <IconBox color="#ba68c8"><PersonIcon size="small" /></IconBox>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary">Guardian</Typography>
                                            <Typography variant="body2" fontWeight="700">{userDetails?.parentName || 'N/A'}</Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                                {userDetails?.advanceTransactions?.length > 0 && (
                                    <Box sx={{ mt: 3, textAlign: 'left' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                            Advance History
                                        </Typography>
                                        <Stack spacing={1}>
                                            {userDetails.advanceTransactions.slice().reverse().slice(0, 5).map((tx, index) => (
                                                <Box key={tx._id || index} sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: tx.type === 'credit' ? '#16a34a' : '#dc2626' }}>
                                                            {tx.type === 'credit' ? 'Added' : 'Used'} Rs.{Number(tx.amount || 0).toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {tx.date || formatDatePart(tx.createdAt)}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                                        Balance: Rs.{Number(tx.balanceAfter || 0).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        </InfoCard>
                    </Grid>

                    {/* Summary and Table */}
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatCard elevation={2}>
                                    <IconBox color="#667eea"><WalletIcon /></IconBox>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Payable Total</Typography>
                                        <Typography variant="h5" fontWeight="800">Rs.{(summary.expected || 0).toLocaleString()}</Typography>
                                    </Box>
                                </StatCard>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatCard elevation={2}>
                                    <IconBox color="#4caf50"><CheckCircleIcon /></IconBox>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Collected</Typography>
                                        <Typography variant="h5" fontWeight="800" color="#4caf50">Rs.{(summary.collected || 0).toLocaleString()}</Typography>
                                    </Box>
                                </StatCard>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatCard elevation={2}>
                                    <IconBox color="#f44336"><WarningIcon /></IconBox>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Outstanding</Typography>
                                        <Typography variant="h5" fontWeight="800" color="#f44336">Rs.{(summary.outstanding || 0).toLocaleString()}</Typography>
                                    </Box>
                                </StatCard>
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <StatCard elevation={2}>
                                    <IconBox color="#2563eb"><WalletIcon /></IconBox>
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Advance Balance</Typography>
                                        <Typography variant="h5" fontWeight="800" color="#2563eb">Rs.{(userDetails?.advanceBalance || 0).toLocaleString()}</Typography>
                                    </Box>
                                </StatCard>
                            </Grid>
                        </Grid>

                        <StyledTableContainer component={Paper} elevation={2}>
                            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
                                <Typography variant="h6" fontWeight="800">Monthly Billing Records</Typography>
                                <Chip label={`${collectionRate}% Cleared`} color="success" size="small" sx={{ fontWeight: 700 }} />
                            </Box>
                            <Table sx={{ minWidth: 600 }}>
                                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableRow>
                                        <StyledTableCell>Month</StyledTableCell>
                                        <StyledTableCell>Amount</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                        <StyledTableCell>Payment Date</StyledTableCell>
                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {feeData?.feeDetails?.map((fee, idx) => {
                                        const styles = getStatusStyles(fee.status);
                                        const financials = getFeeFinancials(fee);
                                        return (
                                            <TableRow 
                                                key={idx} 
                                                hover 
                                                sx={fee.month === highlightMonth ? { bgcolor: '#e0f2fe', transition: 'background-color 0.5s' } : {}}
                                            >
                                                <TableCell sx={{ fontWeight: 700 }}>{fee.month}</TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="700">Billed: Rs.{financials.grossAmount.toLocaleString()}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block', color: '#16a34a', fontWeight: 800 }}>Collected: Rs.{financials.collected.toLocaleString()}</Typography>
                                                        {financials.cashPaid > 0 && (
                                                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Cash Paid: Rs.{financials.cashPaid.toLocaleString()}</Typography>
                                                        )}
                                                        {financials.advancePaid > 0 && (
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#2563eb', fontWeight: 700 }}>Advance Paid: Rs.{financials.advancePaid.toLocaleString()}</Typography>
                                                        )}
                                                        {financials.due > 0 && (
                                                            <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 700 }}>Due: Rs.{financials.due.toLocaleString()}</Typography>
                                                        )}
                                                        {fee.discount > 0 && (
                                                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>Discount: Rs.{fee.discount.toLocaleString()}</Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Toggle Status">
                                                        <StatusChip
                                                            label={fee.status}
                                                            onClick={() => handleToggleStatus(fee)}
                                                            icon={styles.icon}
                                                            sx={{ bgcolor: styles.bg, color: styles.color, fontWeight: 700 }}
                                                        />
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        {fee.paymentDate && (
                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                <CheckCircleIcon sx={{ fontSize: 12, color: '#4caf50' }} />
                                                                <Typography variant="caption" sx={{ color: '#4caf50' }}>{formatDatePart(fee.paymentDate)}</Typography>
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={1} justifyContent="center">
                                                        <IconButton size="small" onClick={() => handleEditClick(fee)} sx={{ color: '#667eea' }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {(!feeData?.feeDetails || feeData.feeDetails.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                                <ReceiptLongIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                                <Typography color="textSecondary">No fee records found for this student.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    </Grid>
                </Grid>
            </Container>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogTitle sx={{ bgcolor: '#f8fafc', fontWeight: 800 }}>Update Billing Detail</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Payment Status</InputLabel>
                                <Select value={editStatus} label="Payment Status" onChange={(e) => {
                                    const val = e.target.value;
                                    setEditStatus(val);
                                    if (val === 'Paid') {
                                        const gross = Math.max(0, parseFloat(editMonthlyAmount || 0) + parseFloat(editDuesAmount || 0) - parseFloat(editDiscountAmount || 0));
                                        const advance = parseFloat(editingFee?.advanceUsed) || 0;
                                        setEditPaidAmount(Math.max(0, gross - advance));
                                    }
                                    else if (val === 'Unpaid') setEditPaidAmount(0);
                                }}>
                                    <MenuItem value="Unpaid">Unpaid</MenuItem>
                                    <MenuItem value="Paid">Paid</MenuItem>
                                    <MenuItem value="Partial">Partial</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Monthly Amount" type="number" value={editMonthlyAmount} onChange={(e) => setEditMonthlyAmount(e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth size="small" label="Dues Amount" type="number" value={editDuesAmount} onChange={(e) => setEditDuesAmount(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Discount Amount" type="number" value={editDiscountAmount} onChange={(e) => setEditDiscountAmount(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Paid Amount" type="number" value={editPaidAmount} onChange={(e) => setEditPaidAmount(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomNepaliDatePicker
                                label="Pay Date"
                                value={editPaymentDate}
                                onChange={(date) => setEditPaymentDate(date)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setEditDialogOpen(false)} sx={{ fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEdit} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px', bgcolor: '#667eea' }}>Save Changes</Button>
                </DialogActions>
            </Dialog>

        </PageContainer>
    );
};

export default ViewStudentFee;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); border-radius: 0 0 40px 40px !important; padding-bottom: 40px;`;
const InfoCard = styled(Paper)`border-radius: 24px !important; overflow: hidden;`;
const StatCard = styled(Paper)`padding: 20px; border-radius: 16px !important; display: flex; align-items: center; gap: 12px; height: 100%;`;
const IconBox = styled.div`width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; background: ${props => `${props.color}15`}; color: ${props => props.color}; & svg { font-size: 20px; }`;
const StyledTableContainer = styled(TableContainer)`border-radius: 20px !important; overflow: hidden;`;
const StyledTableCell = styled(TableCell)`font-weight: 700 !important; color: #64748b !important; padding: 12px 16px !important;`;
const StatusChip = styled(Chip)`height: 24px !important; border-radius: 6px !important; font-size: 0.7rem !important; cursor: pointer;`;
